import { useEffect, useState } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowDataTransferHorizontalIcon,
  CallEnd01Icon,
  Cancel01Icon,
  CheckmarkCircle02Icon,
  DialpadCircle01Icon,
  Mic01Icon,
  MicOff01Icon,
  User02Icon,
  UserAdd01Icon,
  UserCircleIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { Avatar } from "shared/components/Avatar";
import { cx } from "shared/utils";
import {
  UNKNOWN_CALLER_LABEL,
  isCallerUnknown,
  transferSourceLabel
} from "shared/utils/transferred-call";
import { formatPhoneDisplay } from "../views/phone-numbers/lib/format";
import {
  CONSULT_PHASES,
  firstName,
  useSoftphone,
  type ActiveCall,
  type ActiveTransfer,
  type ActiveVoicemailDrop
} from "./SoftphoneProvider";
import { ConsultView } from "./ConsultView";
import { TransferPanel } from "./TransferPanel";
import { VoicemailGlyph } from "shared/components/VoicemailGlyph";
import { VoicemailLeavingStatus, VoicemailPanel } from "./VoicemailPanel";
import { usePickableVoicemails } from "./usePickableVoicemails";
import { CallKeypad } from "./CallKeypad";
import { describeCall, type CallDisplay } from "./callDisplay";
import { SOFTPHONE_OVERLAY_Z_CLASS } from "./stacking";
import { useDraggableOverlay, type DragOffset } from "./useDraggableOverlay";

const ACTIVE_AVATAR_COLOR = "#29B6F6";
const ENDED_AVATAR_COLOR = "#9CA3AF";

interface CallScreenProps {
  // Live in-call duration, ticked once a second by the overlay.
  elapsedSeconds: number;
  // Collapse the expanded card back to the floating pill.
  onMinimize: () => void;
  // Opens the in-call Create-lead form (inbound calls with no matched lead).
  onCreateLead: () => void;
  // Dial-pad visibility, owned by the overlay so the pill can open it too.
  keypadOpen: boolean;
  onKeypadOpenChange: (open: boolean) => void;
  // Drag position, owned by the overlay so it survives minimize/expand.
  dragOffset: DragOffset;
  onDragOffsetChange: (offset: DragOffset) => void;
}

/**
 * Expanded in-call card (CRM-811, Figma 22148:162504; inbound states CRM-905,
 * Figma 23472:90575). Floats bottom-right and walks the call through Ringing →
 * mm:ss (with mute) → No answer / Call ended · mm:ss / Can't reach this
 * number. Inbound calls add a middle action: Contact details (matched lead) or
 * Create lead. Once the call is answered, Keypad opens the DTMF dial pad for
 * IVRs and call screening (CRM-1190). The X collapses a live call to the pill;
 * for a terminal call it dismisses the card. Drag it anywhere on screen to
 * uncover what it sits on top of (CRM-1112).
 */
export function CallScreen({
  elapsedSeconds,
  onMinimize,
  onCreateLead,
  keypadOpen,
  onKeypadOpenChange,
  dragOffset,
  onDragOffsetChange
}: CallScreenProps) {
  const {
    activeCall,
    status,
    isMuted,
    callDurationSeconds,
    hangup,
    toggleMute,
    sendDtmf,
    dismissCall,
    transfer,
    callNotice,
    voicemailDrop
  } = useSoftphone();
  // The call the Transfer call view (CRM-1747) is open for; it replaces the
  // card's body while open.
  const [pickerCallId, setPickerCallId] = useState<number | null>(null);
  // The same for the Leave a voicemail view (CRM-1830).
  const [voicemailPickerCallId, setVoicemailPickerCallId] = useState<
    number | null
  >(null);

  const { nodeRef, dragClassName, style, dragProps } = useDraggableOverlay({
    offset: dragOffset,
    onOffsetChange: onDragOffsetChange
  });

  // Computed before the early return so the hook below can key off it.
  const view = describeCall(status, { elapsedSeconds, callDurationSeconds });
  const keypadAvailable = view.showKeypad;

  // The call ended (or fell back to ringing) with the pad open — there is
  // nothing left to send tones to. Resetting it per call lives in CallOverlay.
  useEffect(() => {
    if (!keypadAvailable) onKeypadOpenChange(false);
  }, [keypadAvailable, onKeypadOpenChange]);

  const canTransfer = view.showMute && !activeCall?.transferUnavailable;
  const offerVoicemail = canOfferVoicemail(activeCall, view, transfer);
  const voicemails = usePickableVoicemails(offerVoicemail);

  if (!activeCall) return null;

  const displayName = activeCall.toLabel?.trim();
  const isInbound = activeCall.direction === "inbound";
  const { displayNumber, transferSource } = describeParty(activeCall);
  const callerName = displayName || displayNumber;
  // A transfer card only ever describes this call — never a newer one taken
  // while it was still on screen.
  const ownTransfer = transfer?.callId === activeCall.callId ? transfer : null;
  const body =
    voicemailBody({
      drop: voicemailDrop?.callId === activeCall.callId ? voicemailDrop : null,
      pickerOpen: voicemailPickerCallId === activeCall.callId && offerVoicemail,
      callerName,
      displayName,
      elapsedLabel: view.statusLine,
      onClosePicker: () => setVoicemailPickerCallId(null)
    }) ??
    transferBody({
      transfer: ownTransfer,
      // Only on a live, answered call; a transfer that started closes it.
      pickerOpen: pickerCallId === activeCall.callId && canTransfer,
      callerName,
      elapsedLabel: view.statusLine,
      durationSeconds: callDurationSeconds,
      onDismiss: dismissCall,
      onClosePicker: () => setPickerCallId(null)
    });

  return (
    <div
      ref={nodeRef}
      role="dialog"
      aria-label="Active call"
      style={style}
      {...dragProps}
      className={cx(
        // The open dial pad makes the card roughly twice as tall, so cap it to
        // the viewport instead of letting it run off the top of short screens.
        "fixed bottom-4 right-4 max-h-[calc(100vh-2rem)] w-[360px] max-w-[calc(100vw-2rem)] overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl ring-1 ring-alpha-100",
        dragClassName,
        SOFTPHONE_OVERLAY_Z_CLASS
      )}
    >
      <button
        type="button"
        aria-label={view.isTerminal ? "Dismiss" : "Minimize"}
        onClick={view.isTerminal ? dismissCall : onMinimize}
        className="absolute right-3 top-3 grid size-8 place-items-center rounded-full text-content-300 transition hover:bg-alpha-40 hover:text-content-1000"
      >
        <HugeiconsIcon icon={Cancel01Icon} size={20} />
      </button>

      {body ?? (
        <div className="flex flex-col items-center text-center">
          <PartyHeader
            displayName={displayName}
            displayNumber={displayNumber}
            transferSource={transferSource}
            live={view.isLive}
          />
          <StatusBlock view={view} notice={callNotice} />

          {keypadOpen ? (
            <CallKeypad
              onDigit={sendDtmf}
              onClose={() => onKeypadOpenChange(false)}
            />
          ) : null}

          <CallControls
            view={view}
            isMuted={isMuted}
            onHangup={hangup}
            onToggleMute={toggleMute}
            keypadOpen={keypadOpen}
            onToggleKeypad={() => onKeypadOpenChange(!keypadOpen)}
            // Lead actions only make sense on an answered inbound call (CRM-905).
            leadId={isInbound && view.showMute ? activeCall.leadId : undefined}
            onCreateLead={
              isInbound && view.showMute && !activeCall.leadId
                ? onCreateLead
                : undefined
            }
            onTransfer={
              canTransfer ? () => setPickerCallId(activeCall.callId) : undefined
            }
            voicemail={voicemailControl({
              offer: offerVoicemail,
              count: voicemails.data?.length ?? 0,
              loading: voicemails.isLoading,
              failed: voicemails.isError,
              onOpen: () => setVoicemailPickerCallId(activeCall.callId)
            })}
          />
        </div>
      )}
    </div>
  );
}

function PartyHeader({
  displayName,
  displayNumber,
  transferSource,
  live
}: {
  displayName: string | undefined;
  displayNumber: string;
  transferSource: string | null;
  live: boolean;
}) {
  return (
    <>
      {/* Initials only for a known label; a bare number gets the silhouette
          (Jon, 2026-08-24) — it must not go into `name` or it turns into
          "+(" initials. */}
      <Avatar
        name={displayName}
        size={72}
        className="text-2xl font-semibold"
        fallbackText={<HugeiconsIcon icon={User02Icon} size={32} />}
        fallbackBackgroundColor={
          live ? ACTIVE_AVATAR_COLOR : ENDED_AVATAR_COLOR
        }
      />

      <p className="mt-4 text-base font-semibold text-content-1000">
        {displayNumber}
      </p>
      {displayName ? (
        <p className="mt-0.5 text-sm text-content-400">{displayName}</p>
      ) : null}
      {transferSource ? (
        <p className="mt-1 text-xs text-content-400">
          Transferred from {transferSource}
        </p>
      ) : null}
    </>
  );
}

function StatusBlock({
  view,
  notice
}: {
  view: CallDisplay;
  // CRM-1747: e.g. "Justin didn't answer. You're back with the caller."
  notice: string | null;
}) {
  return (
    <div className="mt-8 min-h-12">
      <p
        className={cx(
          "text-lg font-semibold tabular-nums",
          view.tone === "error" ? "text-danger-500" : "text-content-1000"
        )}
      >
        {view.statusLine}
      </p>
      {view.detail ? (
        <p className="mx-auto mt-1 max-w-[260px] text-xs text-danger-400">
          {view.detail}
        </p>
      ) : null}
      {notice ? (
        <p className="mx-auto mt-1 max-w-[280px] text-xs text-warning-600">
          {notice}
        </p>
      ) : null}
    </div>
  );
}

// CRM-1830: Leave voicemail is only on an answered call this teammate placed
// and nobody has transferred. Incoming calls, the mobile app and the AI agent
// never get it.
function canOfferVoicemail(
  activeCall: ActiveCall | null,
  view: CallDisplay,
  transfer: ActiveTransfer | null
): boolean {
  if (!activeCall || !view.showMute) return false;
  if (activeCall.direction !== "outbound") return false;
  if (activeCall.transferredByName || activeCall.transferStarted) return false;
  return transfer?.callId !== activeCall.callId;
}

// The card's Leave voicemail button, when the call offers one (CRM-1830). A
// list that failed to load stays openable: the list says so there.
function voicemailControl({
  offer,
  count,
  loading,
  failed,
  onOpen
}: {
  offer: boolean;
  count: number;
  loading: boolean;
  failed: boolean;
  onOpen: () => void;
}): CallControlsProps["voicemail"] {
  if (!offer) return undefined;
  return {
    available: count > 0 || failed,
    showEmptyHint: !loading && !failed && count === 0,
    onOpen
  };
}

// What replaces the card's body for a pre-recorded voicemail (CRM-1830): the
// message just left, or the list to pick one from. Null keeps the rest.
function voicemailBody({
  drop,
  pickerOpen,
  callerName,
  displayName,
  elapsedLabel,
  onClosePicker
}: {
  drop: ActiveVoicemailDrop | null;
  pickerOpen: boolean;
  callerName: string;
  displayName: string | undefined;
  elapsedLabel: string;
  onClosePicker: () => void;
}) {
  if (drop)
    return (
      <VoicemailLeavingStatus
        drop={drop}
        callerName={callerName}
        displayName={displayName}
      />
    );
  if (pickerOpen)
    return (
      <VoicemailPanel
        callerName={callerName}
        elapsedLabel={elapsedLabel}
        onClose={onClosePicker}
      />
    );
  return null;
}

// What replaces the card's body during a transfer (CRM-1747): the Talk first
// consult, how a Transfer now went, or the Transfer call view. Null keeps the
// regular in-call body.
function transferBody({
  transfer,
  pickerOpen,
  callerName,
  elapsedLabel,
  durationSeconds,
  onDismiss,
  onClosePicker
}: {
  transfer: ActiveTransfer | null;
  pickerOpen: boolean;
  callerName: string;
  elapsedLabel: string;
  durationSeconds: number | null;
  onDismiss: () => void;
  onClosePicker: () => void;
}) {
  if (transfer && CONSULT_PHASES.includes(transfer.phase))
    return <ConsultView transfer={transfer} callerName={callerName} />;
  if (transfer)
    return (
      <TransferStatus
        transfer={transfer}
        callerName={callerName}
        durationSeconds={durationSeconds}
        onClose={onDismiss}
      />
    );
  if (pickerOpen)
    return (
      <TransferPanel
        callerName={callerName}
        elapsedLabel={elapsedLabel}
        onClose={onClosePicker}
      />
    );
  return null;
}

/**
 * The card after a Transfer now (CRM-1747): the teammate's own leg ended the
 * moment the call moved, so this only reports how it went. It closes on its
 * own once the transfer settles.
 */
function TransferStatus({
  transfer,
  callerName,
  durationSeconds,
  onClose
}: {
  transfer: ActiveTransfer;
  callerName: string;
  durationSeconds: number | null;
  onClose: () => void;
}) {
  const first = firstName(transfer.targetName);
  // CallScreen shows the consult phases in ConsultView, never here.
  const describe =
    TRANSFER_STATUS_COPY[transfer.phase as StatusPhase] ??
    TRANSFER_STATUS_COPY.transferred;
  const copy = describe(transfer.targetName, first, callerName);
  const done = transfer.phase === "transferred";

  return (
    <div className="flex flex-col items-center py-4 text-center">
      {done ? (
        <span className="grid size-[72px] place-items-center rounded-full bg-success-50 text-success-500">
          <HugeiconsIcon icon={CheckmarkCircle02Icon} size={36} />
        </span>
      ) : (
        <Avatar name={transfer.targetName} size={72} className="text-2xl" />
      )}
      <p className="mt-4 text-base font-semibold text-content-1000">
        {copy.title}
      </p>
      <p className="mx-auto mt-1 max-w-[280px] text-sm text-content-400">
        {done && durationSeconds !== null
          ? `${copy.detail} · ${formatClock(durationSeconds)}`
          : copy.detail}
      </p>
      <Button
        className="mt-6"
        size={ButtonSize.SM}
        variant={ButtonVariant.BASE}
        onClick={onClose}
      >
        Close
      </Button>
    </div>
  );
}

type StatusPhase = Exclude<
  ActiveTransfer["phase"],
  | "consult-ringing"
  | "consulting"
  | "consult-not-answered"
  | "consult-destination-left"
>;

const TRANSFER_STATUS_COPY: Record<
  StatusPhase,
  (
    name: string,
    first: string,
    caller: string
  ) => { title: string; detail: string }
> = {
  transferring: (_name, first, caller) => ({
    title: `Transferring to ${first}…`,
    detail: `${caller} hears ringing. If ${first} doesn't answer in 30 seconds, the call comes back to you.`
  }),
  transferred: (name) => ({
    title: `Transferred to ${name}`,
    detail: "You've left the call"
  }),
  returning: (_name, first) => ({
    title: `${first} didn't answer`,
    detail: "Bringing the caller back to you…"
  }),
  "caller-left": (_name, first) => ({
    title: "The caller hung up",
    detail: `They left before ${first} answered.`
  })
};

const formatClock = (seconds: number) =>
  `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;

/**
 * What the card prints for the other party: their number — or an honest
 * "Unknown caller" for an escalated call the api could not attribute
 * (CRM-1393) — and the AI agent line an inbound call came through.
 */
function describeParty(activeCall: ActiveCall): {
  displayNumber: string;
  transferSource: string | null;
} {
  if (activeCall.direction !== "inbound")
    return {
      displayNumber: formatPhoneDisplay(activeCall.toNumber),
      // An outbound call a teammate transferred to you rings in as incoming
      // (CRM-1747), so only that case names a sender here.
      transferSource: activeCall.transferredByName?.trim() || null
    };

  const callerUnknown = isCallerUnknown(
    activeCall.toNumber,
    activeCall.transferredFromNumber
  );
  return {
    displayNumber: callerUnknown
      ? UNKNOWN_CALLER_LABEL
      : formatPhoneDisplay(activeCall.toNumber),
    transferSource: transferSourceLabel(
      activeCall.transferredFromNumber,
      activeCall.transferredFromName,
      activeCall.transferredByName
    )
  };
}

interface CallControlsProps {
  view: CallDisplay;
  isMuted: boolean;
  onHangup: () => void;
  onToggleMute: () => void;
  keypadOpen: boolean;
  onToggleKeypad: () => void;
  // Inbound-only middle action: link to the matched lead…
  leadId?: number;
  // …or open the in-call Create-lead form when nothing matched.
  onCreateLead?: () => void;
  // Opens the Transfer call view (CRM-1747) on an answered call.
  onTransfer?: () => void;
  // CRM-1830: Leave voicemail on an answered outgoing call; greyed out while
  // the company has no messages.
  voicemail?: {
    available: boolean;
    showEmptyHint: boolean;
    onOpen: () => void;
  };
}

function CallControls({
  view,
  onHangup,
  voicemail,
  ...rowProps
}: CallControlsProps) {
  if (!view.showHangup && !view.showMute) return null;

  return (
    <div className="mt-6 space-y-5">
      {/* An answered call shows up to four controls in this row (Contact
          details · Keypad · Mute · Transfer), so End call sits on its own
          below it (CRM-1747). */}
      {view.showMute ? <AnsweredControls view={view} {...rowProps} /> : null}
      {voicemail ? <LeaveVoicemailButton {...voicemail} /> : null}
      {view.showHangup ? (
        <div className="flex justify-center">
          <ControlButton
            label="End call"
            onClick={onHangup}
            circleClassName="bg-danger-500 text-white hover:bg-danger-600"
            icon={CallEnd01Icon}
          />
        </div>
      ) : null}
    </div>
  );
}

// The full-width button between the round controls and End call (CRM-1830,
// option A in the mockup).
function LeaveVoicemailButton({
  available,
  showEmptyHint,
  onOpen
}: {
  available: boolean;
  showEmptyHint: boolean;
  onOpen: () => void;
}) {
  const disabled = !available;
  return (
    <div className="space-y-2">
      <button
        type="button"
        disabled={disabled}
        aria-disabled={disabled}
        onClick={onOpen}
        className={cx(
          "flex h-10 w-full items-center justify-center gap-2 rounded-lg border text-xs font-bold transition",
          disabled
            ? "cursor-not-allowed border-alpha-40 bg-alpha-20 text-content-200"
            : "border-alpha-100 bg-white text-content-1000 hover:bg-alpha-40"
        )}
      >
        <VoicemailGlyph />
        <span>Leave voicemail</span>
      </button>
      {showEmptyHint ? (
        <p className="text-xs text-content-400">
          No pre-recorded voicemails yet. An admin can add them in Phone numbers
          → Pre-recorded voicemails.
        </p>
      ) : null}
    </div>
  );
}

function AnsweredControls({
  view,
  isMuted,
  onToggleMute,
  keypadOpen,
  onToggleKeypad,
  leadId,
  onCreateLead,
  onTransfer
}: Omit<CallControlsProps, "onHangup" | "voicemail">) {
  return (
    <div className="flex items-start justify-center gap-6">
      <LeadAction leadId={leadId} onCreateLead={onCreateLead} />
      {view.showKeypad ? (
        <ControlButton
          label="Keypad"
          onClick={onToggleKeypad}
          pressed={keypadOpen}
          circleClassName={cx(
            "text-content-1000 hover:bg-alpha-100",
            keypadOpen ? "bg-alpha-100" : "bg-alpha-40"
          )}
          icon={DialpadCircle01Icon}
        />
      ) : null}
      <ControlButton
        label={isMuted ? "Unmute" : "Mute"}
        onClick={onToggleMute}
        circleClassName={cx(
          "text-content-1000 hover:bg-alpha-100",
          isMuted ? "bg-alpha-100" : "bg-alpha-40"
        )}
        icon={isMuted ? MicOff01Icon : Mic01Icon}
      />
      {onTransfer ? (
        <ControlButton
          label="Transfer"
          onClick={onTransfer}
          circleClassName="bg-alpha-40 text-content-1000 hover:bg-alpha-100"
          icon={ArrowDataTransferHorizontalIcon}
        />
      ) : null}
    </div>
  );
}

function LeadAction({
  leadId,
  onCreateLead
}: {
  leadId?: number;
  onCreateLead?: () => void;
}) {
  if (leadId)
    return (
      // Plain anchor (the overlay mounts outside the router) opening a NEW
      // tab — a same-tab navigation would unload the SPA and destroy the
      // Twilio Device, dropping the live call.
      <a
        href={`/leads/${leadId}`}
        target="_blank"
        rel="noreferrer"
        className="flex flex-col items-center gap-2"
      >
        <span className="grid size-12 place-items-center rounded-full bg-alpha-40 text-content-1000 transition hover:bg-alpha-100">
          <HugeiconsIcon icon={UserCircleIcon} size={22} />
        </span>
        <span className="text-xs text-content-400">Contact details</span>
      </a>
    );
  if (onCreateLead)
    return (
      <ControlButton
        label="Create lead"
        onClick={onCreateLead}
        circleClassName="bg-alpha-40 text-content-1000 hover:bg-alpha-100"
        icon={UserAdd01Icon}
      />
    );
  return null;
}

interface ControlButtonProps {
  label: string;
  onClick: () => void;
  circleClassName: string;
  icon: typeof CallEnd01Icon;
  // Set on controls that toggle a panel (Keypad) — the label stays put, so
  // aria-pressed is what tells a screen reader the pad is open.
  pressed?: boolean;
}

function ControlButton({
  label,
  onClick,
  circleClassName,
  icon,
  pressed
}: ControlButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={pressed}
      className="flex flex-col items-center gap-2"
    >
      <span
        className={cx(
          "grid size-12 place-items-center rounded-full transition",
          circleClassName
        )}
      >
        <HugeiconsIcon icon={icon} size={22} />
      </span>
      <span className="text-xs text-content-400">{label}</span>
    </button>
  );
}
